import React from 'react';
import { Icon } from '@livecommerce/design-system';
import { AddIcon, DownloadIcon } from '../icons/index.js';
import { cx } from '@linaria/core';
import Loader from '../loader/index.jsx';
import { oneOf, bool, object, func, element, oneOfType, string, array } from 'prop-types';
import {
  rootClassname,
  actionClassname,
  iconClassname,
  contentClassname,
  contentButtonAsText,
  iconWithContent,
  iconWithoutContent,
  sizeM,
  sizeS,
  sizeSquare,
  smallSquare, classnamesByType,
} from './styles.js';

const Button = ({
  type,
  children,
  onClick,
  disabled = false,
  loading = false,
  icon = null,
  classes,
  style: overwriteStyles = {},
  contentStyle = {},
  size = 'L',
  className,
  dataTestId = null,
  ...attr
}) => {
  const bundledClassNames = cx(
    rootClassname,
    disabled ? null : classnamesByType[type],
    type === 'action' && actionClassname,
    type === 'icon' && iconClassname,
    icon && !children && iconWithoutContent,
    size === 'M' && sizeM,
    size === 'S' && sizeS,
    size === 'square' && sizeSquare,
    size === 'smallSquare' && smallSquare,
    className,
  );

  const classesOfContent = cx(contentClassname, type === 'text' && contentButtonAsText);
  const color = (type === 'primary' || type === 'live') ? 'white' : 'black';

  if (typeof icon === 'string') {
    switch (icon) {
      case 'plus':
      case 'add':
        icon = <AddIcon color={color} />;
        break;
      case 'plus-small':
      case 'add-small':
        icon = <AddIcon type="small" color={color} />;
        break;
      case 'download':
        icon = <DownloadIcon color={color} />;
        break;
      case 'download-small':
        icon = <DownloadIcon type="small" color={color} />;
        break;
      case 'cross-small':
        icon = <Icon name="cross" color={color} height={12} width={12} />;
        break;
      default:
        icon = null;
    }
  }

  return (
    <button className={bundledClassNames} disabled={disabled} onClick={onClick} style={overwriteStyles} data-test-id={dataTestId} {...attr}>
      {type === 'icon' ? icon : (
        <span className={classesOfContent} style={{ ...contentStyle, ...loading ? { visibility: 'hidden' } : {} }}>
          {icon && <span className={cx(!!children && iconWithContent)}>{icon}</span>}
          {children}
        </span>
      )}
      {!!loading && <Loader type="small" color={color} />}
    </button>
  );
};

Button.propTypes = {
  type: oneOf(['primary', 'secondary', 'action', 'text', 'live', 'icon']),
  children: oneOfType([element, string, array]),
  onClick: func,
  disabled: bool,
  loading: bool,
  icon: oneOfType([
    oneOf(['plus', 'add', 'plus-small', 'add-small', 'download', 'download-small', 'cross-small']),
    element,
  ]),
  classes: object,
  style: object,
  size: oneOf(['L', 'M', 'S', 'square', 'smallSquare']),
  className: string,
  dataTestId: string,
};

export default Button;
